type UpdateAvailable = (worker: ServiceWorker) => void;
type Subscription = { activate: (worker: ServiceWorker) => void; dispose: () => void };
const UPDATE_RECHECK_MS = 60 * 60 * 1000;

/** Production-only consumer registration. Update is opt-in; never reload mid-action. */
export function registerConsumerPwa(onUpdate: UpdateAvailable): Subscription {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) {
    return { activate: () => {}, dispose: () => {} };
  }
  let disposed = false;
  let registration: ServiceWorkerRegistration | null = null;
  let tracked: ServiceWorker | null = null;
  let checkedAt = 0, requestedReload = false, reloaded = false;
  const offer = () => {
    if (disposed || !navigator.serviceWorker.controller) return;
    if (registration?.waiting) onUpdate(registration.waiting);
  };
  const onWorkerStateChange = () => { if (tracked?.state === 'installed') offer(); };
  const trackInstalling = () => {
    tracked?.removeEventListener('statechange', onWorkerStateChange);
    tracked = registration?.installing ?? null;
    tracked?.addEventListener('statechange', onWorkerStateChange);
    offer();
  };
  const onControllerChange = () => {
    if (!requestedReload || reloaded) return;
    reloaded = true;
    window.location.reload();
  };
  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
  const check = async (force = false) => {
    if (!registration || disposed || !navigator.onLine) return;
    if (!force && Date.now() - checkedAt < UPDATE_RECHECK_MS) return;
    checkedAt = Date.now();
    try { await registration.update(); }
    catch { /* Network problems must never interrupt the live app. */ }
    offer();
  };
  const visibility = () => { if (document.visibilityState === 'visible') void check(); };
  document.addEventListener('visibilitychange', visibility);
  void navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' })
    .then(next => {
      if (disposed) return;
      registration = next;
      registration.addEventListener('updatefound', trackInstalling);
      trackInstalling();
      offer();
      void check(true);
    })
    .catch(() => { /* Progressive enhancement: ordinary web app still functions. */ });
  return {
    activate: worker => {
      if (disposed || !registration || registration.waiting !== worker) return;
      requestedReload = true;
      worker.postMessage({ type: 'HOPPER_PWA_ACTIVATE' });
    },
    dispose: () => {
      disposed = true;
      registration?.removeEventListener('updatefound', trackInstalling);
      tracked?.removeEventListener('statechange', onWorkerStateChange);
      document.removeEventListener('visibilitychange', visibility);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    },
  };
}
